import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { matchPath } from "react-router-dom";

// All returns processing routes are fullscreen for warehouse workers on tablets/iPads
const FULLSCREEN_ROUTES: string[] = [
  "/returns/processing",
  "/returns/processing/:returnLabel",
  "/returns/processing/:returnLabel/inspect/:sessionItemId",
  "/returns/processing-flow",
  "/returns/processing-flow/:returnLabel",
  "/returns/processing-flow/:returnLabel/flow/:sessionItemId",
];

function isFullscreenRoute(url: string): boolean {
  return FULLSCREEN_ROUTES.some(
    (route) => !!matchPath(`/stores/:storeId${route}`, url),
  );
}

const createFullscreenAwarePage = async () => {
  const { Page } = await import("../../page");
  return {
    element: (
      <Page
        hideHeader={isFullscreenRoute}
        hideNavbar={isFullscreenRoute}
        hidePadding={isFullscreenRoute}
      />
    ),
  };
};

export const returnsProcessingRoutes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: "Returns Processing" },
    lazy: createFullscreenAwarePage,
    children: [
      {
        async lazy() {
          const { ReturnsProcessingProviders } = await import(
            "@redotech/redo-merchant-returns-processing-app/returns-processing-providers"
          );
          return { Component: ReturnsProcessingProviders };
        },
        children: [
          {
            index: true,
            async lazy() {
              const { ReturnLookupPage } = await import(
                "@redotech/redo-merchant-returns-processing-app/lookup-page"
              );
              return { Component: ReturnLookupPage };
            },
          },
          {
            path: ":returnLabel",
            async lazy() {
              const { ProcessingProviders } = await import(
                "@redotech/redo-merchant-returns-processing-app/processing-providers"
              );
              return { Component: ProcessingProviders };
            },
            children: [
              {
                index: true,
                handle: { breadcrumb: "Process Return" },
                async lazy() {
                  const { ReturnProcessingPage } = await import(
                    "@redotech/redo-merchant-returns-processing-app/processing-page"
                  );
                  return { Component: ReturnProcessingPage };
                },
              },
              {
                path: "inspect/:sessionItemId",
                handle: { breadcrumb: "Inspect Item" },
                async lazy() {
                  const { ItemInspectionPage } = await import(
                    "@redotech/redo-merchant-returns-processing-app/inspection-page"
                  );
                  return { Component: ItemInspectionPage };
                },
              },
            ],
          },
        ],
      },
    ],
  },
];

export const returnsProcessingFlowRoutes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: "Returns Processing Flow" },
    lazy: createFullscreenAwarePage,
    children: [
      {
        async lazy() {
          const { ReturnsProcessingProviders } = await import(
            "@redotech/redo-merchant-returns-processing-app/returns-processing-providers"
          );
          return { Component: ReturnsProcessingProviders };
        },
        children: [
          {
            index: true,
            async lazy() {
              const { ReturnLookupPage } = await import(
                "@redotech/redo-merchant-returns-processing-app/lookup-page"
              );
              return { Component: ReturnLookupPage };
            },
          },
          {
            path: ":returnLabel",
            async lazy() {
              const { ProcessingProviders } = await import(
                "@redotech/redo-merchant-returns-processing-app/processing-providers"
              );
              return { Component: ProcessingProviders };
            },
            children: [
              {
                index: true,
                handle: { breadcrumb: "Process Return" },
                async lazy() {
                  const { ReturnProcessingFlowPage } = await import(
                    "@redotech/redo-merchant-returns-processing-app/processing-page-flow"
                  );
                  return { Component: ReturnProcessingFlowPage };
                },
              },
              {
                path: "flow/:sessionItemId",
                handle: { breadcrumb: "Flow Step" },
                async lazy() {
                  const { FlowStepPage } = await import(
                    "@redotech/redo-merchant-returns-processing-app/flow-step-page"
                  );
                  return { Component: FlowStepPage };
                },
              },
            ],
          },
        ],
      },
    ],
  },
];
